import { reactive } from '@vue/composition-api';
import settouReportApi from '@/apis/johaisetsu/settouPatrol/settouReport/johaisetsu_settou_report';
import type {
  SettouPatrolReport,
} from '@/models/apis/johaisetsu/settouPatrol/settouPatrol/johaisetsuSettouPatrolReportsResponse';
import type {
  SettouOnsiteManagementReport,
} from '@/models/apis/johaisetsu/settouPatrol/settouOnsiteManagement/johaisetsuSettouOnsiteManagementReportsResponse';
import type { SettouPatrolReportRaw } from '@/models/johaisetsu/settouPatrol/settouPatrol/johaisetsuSettouPatrol';
import type {
  SettouOnsiteManagementReportRaw,
} from '@/models/johaisetsu/settouPatrol/settouOnsiteManagement/johaisetsuSettouOnsiteManagementReport';
import type { RoadNameDirection } from '@/models';
import type { SettouPatrolReportRoadConditionMap } from '@/models/apis/johaisetsu/johaisetsuCommon';
import type { JohaisetsuAreaName, SettouWorkType } from '@/lib/johaisetsu/johaisetsuCommonUtil';
import { SETTOU_PATROL_DISPLAY_NAME } from '@/lib/johaisetsu/johaisetsuCommonUtil';
import type { SettouReport } from '@/models/johaisetsu/settouPatrol/settouReport/johaisetsuSettouReport';
import {
  SETTOU_REPORT_TYPE_PATROL,
  SETTOU_REPORT_TYPE_ONSITE_MANAGEMENT,
} from '@/models/johaisetsu/settouPatrol/settouReport/johaisetsuSettouReport';

export interface SettouReportsFetchParams {
  tsFrom: Date;
  tsTo: Date;
  johaisetsuArea: JohaisetsuAreaName | null;
  johaisetsuHanNames: string[];
  workTypes: SettouWorkType[];
  roadNameReals: string[];
  direction: string;
  roadCondition: string;
  roadNameMap: Record<string, RoadNameDirection>;
  roadConditionMap: Record<string, SettouPatrolReportRoadConditionMap>;
}

interface ReportsState {
  patrolReports: SettouPatrolReportRaw[];
  onsiteReports: SettouOnsiteManagementReportRaw[];
}

interface UseSettouReportsResult {
  fetchReports: (params: SettouReportsFetchParams) => Promise<void>;
  clearReports: () => void;
  buildRows: () => SettouReport[];
}

export function useSettouReports(): UseSettouReportsResult {
  const reportsState = reactive<ReportsState>({
    patrolReports: [],
    onsiteReports: [],
  });

  const convPatrolReports = (
    reports: SettouPatrolReport[],
    roadNameMap: Record<string, RoadNameDirection>,
    roadConditionMap: Record<string, SettouPatrolReportRoadConditionMap>,
  ): SettouPatrolReportRaw[] => {
    return reports.map(report => {
      const roadNameDisp = roadNameMap[report.road_name_disp]?.roadNameDisp ?? report.road_name_disp;
      let roadConditionText = '';
      const roadConditionObj = roadConditionMap[report.road_condition];
      if (roadConditionObj) {
        roadConditionText = roadConditionObj.text;
        const conditionsWithCm = [7, 8];
        if (conditionsWithCm.includes(parseInt(report.road_condition))) {
          roadConditionText = roadConditionText.replace(/\$\$/, report.snow_height.toString());
        }
      }
      return {
        ...report,
        ts: new Date(report.ts),
        roadNameDisp,
        roadConditionText,
        hasImage: report.photos.length > 0,
      };
    });
  };

  const convOnsiteReports = (
    reports: SettouOnsiteManagementReport[],
    roadNameMap: Record<string, RoadNameDirection>,
  ): SettouOnsiteManagementReportRaw[] => {
    return reports.map(report => {
      const roadNameDisp = roadNameMap[report.road_name_disp]?.roadNameDisp ?? report.road_name_disp;
      return {
        ...report,
        ts: new Date(report.ts),
        roadNameDisp,
        workArea: report.work_area ?? '',
        hasImage: report.photos.length > 0,
      };
    });
  };

  const buildPatrolContentLines = (report: SettouPatrolReportRaw): string[] => {
    const kpDisplay = report.kp > 0 ? `${report.kp}km` : '';
    const temperature = report.temperature ?? '';
    const roadTemperature = report.road_temperature ?? '';
    const lines = [
      `担当者: ${report.manager_name}  連絡先: ${report.phone_number}`,
      `報告地点: ${report.roadNameDisp}${report.direction ? ` (${report.direction})` : ''} ${kpDisplay}`,
      `路面状況: ${report.road_condition} ${report.roadConditionText}`,
      `積雪: ${report.snow_height}cm  天候: ${report.weather}  気温: ${temperature}℃  路温: ${roadTemperature}℃`,
    ];
    if (report.bikou1) {
      lines.push(`備考: ${report.bikou1}`);
    }
    return lines;
  };

  const buildOnsiteContentLines = (report: SettouOnsiteManagementReportRaw): string[] => {
    return [
      `担当者: ${report.manager_name}  連絡先: ${report.phone_number}`,
      `作業路線: ${report.roadNameDisp}  作業工区: ${report.workArea}`,
      `作業状況: ${report.work_progress}`,
    ];
  };

  const fetchReports = async(params: SettouReportsFetchParams): Promise<void> => {
    const { data } = await settouReportApi.index({
      ts_from: params.tsFrom,
      ts_to: params.tsTo,
      work_types: params.workTypes,
      johaisetsu_area: params.johaisetsuArea ?? undefined,
      johaisetsu_han_names: params.johaisetsuHanNames,
      road_name_disp: params.roadNameReals.length > 0 ? params.roadNameReals : undefined,
      direction: params.direction ?? undefined,
      road_condition: params.roadCondition ?? undefined,
    });
    if (!data) return;

    reportsState.patrolReports = convPatrolReports(data.patrol_reports, params.roadNameMap, params.roadConditionMap);
    reportsState.onsiteReports = convOnsiteReports(data.onsite_management_reports, params.roadNameMap);
  };

  const clearReports = (): void => {
    reportsState.patrolReports = [];
    reportsState.onsiteReports = [];
  };

  const buildRows = (): SettouReport[] => {
    const patrolRows = reportsState.patrolReports
      .map((r): SettouReport => ({
        id: r.id,
        ts: r.ts,
        workTypeDisplay: SETTOU_PATROL_DISPLAY_NAME,
        johaisetsuHanName: r.johaisetsu_han_name ?? '',
        contentLines: buildPatrolContentLines(r),
        hasImage: r.hasImage,
        reportType: SETTOU_REPORT_TYPE_PATROL,
        rawReport: r,
      }));

    const onsiteRows = reportsState.onsiteReports
      .map((report): SettouReport => ({
        id: report.id,
        ts: report.ts,
        workTypeDisplay: report.work_type as SettouWorkType,
        johaisetsuHanName: report.johaisetsu_han_name ?? '',
        contentLines: buildOnsiteContentLines(report),
        hasImage: report.hasImage,
        reportType: SETTOU_REPORT_TYPE_ONSITE_MANAGEMENT,
        rawReport: report,
      }));

    return [...patrolRows, ...onsiteRows].sort((a, b) => b.ts.getTime() - a.ts.getTime());
  };

  return { fetchReports, clearReports, buildRows };
}
